import { BrowserRouter, Route, Routes, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useLayoutEffect } from 'react';
import useAuthContext from './contexts/Auth/useAuthContext';
import { canonicalizeBareNewBasename, isSessionPublicPath, redirectClearedSessionToLogin } from './helpers/sessionPublicPath';
import RouterGoogleAnalyticsTracker from './components/GoogleAnalyticsTracker/RouterGoogleAnalyticsTracker';
import CookieConsentBanner from './components/CookieConsentBanner/CookieConsentBanner';
import PrivacyPolicyPage from './pages/PrivacyPolicy/PrivacyPolicyPage';
import RegulationsPage, { REGULATIONS_PAGE_PATH } from './pages/Regulations/RegulationsPage';
import Layout from './Layout';
import LoginPage from './pages/authPages/LoginPage';
import UserPage from './pages/User/UserPage';
import UserSettingsPage from './pages/User/UserSettingsPage';
import SigninPage from './pages/authPages/SigninPage';
import ForgotPasswordPage from './pages/authPages/ForgotPasswordPage';
import RenewForgotPasswordPage from './pages/authPages/RenewForgotPasswordPage';
import TransactionPage from './pages/Transactions/TransactionPage';
import SupportRequestsPage from './pages/SupportRequests/SupportRequestsPage';
import SupportedsPage from './pages/Supporteds/SupportedsPage';
import SupportGoalsPage from './pages/SupportGoals/SupportGoalsPage';
import SupportRequestsManagementPage from './pages/Management/SupportRequests/SupportRequestsManagementPage';
import ClassificationsManagementPage from './pages/Management/ClassificationsManagement/ClassificationsManagementPage';
import ChildrenManagementPage from './pages/Management/ChildrenManagement/ChildrenManagementPage';
import SupportedsManagementPage from './pages/Management/SupportedsManagement/SupportedsManagementPage';
import HolidayPeriodsManagementPage from './pages/Management/HolidayPeriods/HolidayPeriodsManagementPage';
import ProjectsPage from './pages/Projects/ProjectsPage';
import NewProjectPage from './pages/Projects/NewProjectPage';
import KollelsPage from './pages/Kollels/KollelsPage';
import FundPaymentDevicesPage from './pages/PaymentDevices/FundPaymentDevicesPage';
import PaymentDevicesManagementPage from './pages/Management/PaymentDevices/PaymentDevicesManagementPage';
import NewSupplierPage from './pages/Projects/NewSupplierPage';
import ProjectSuppliersPage from './pages/Projects/ProjectSuppliersPage';
import ProjectsManagementPage from './pages/Management/Projects/ProjectsManagementPage';
import KollelsManagementPage from './pages/Management/Kollels/KollelsManagementPage';
import KollelFoldersManagementPage from './pages/Management/Kollels/KollelFoldersManagementPage';
import SuppliersApprovalPage from './pages/Management/Projects/SuppliersApprovalPage';
import ActivityDomainsManagementPage from './pages/Management/ActivityDomains/ActivityDomainsManagementPage';
import ProjectTypesManagementPage from './pages/Management/ProjectTypes/ProjectTypesManagementPage';
import NedarimSyncDashboardPage from './pages/Management/NedarimSync/NedarimSyncDashboardPage';
import BankCollectionLocksPage from './pages/Management/BankCollectionLocks/BankCollectionLocksPage';
import BulkUploadsPage from './pages/Management/BulkUploads/BulkUploadsPage';
import EligibilityOverridesManagementPage from './pages/Management/EligibilityOverrides/EligibilityOverridesManagementPage';
import ThirdPartyPaymentAuthorizationsManagementPage from './pages/Management/ThirdPartyPaymentAuthorizations/ThirdPartyPaymentAuthorizationsManagementPage';
import SuppliersManagementPage from './pages/Management/Suppliers/SuppliersManagementPage';
import OpenFundRequestsListPage from './pages/Management/OpenFundRequests/OpenFundRequestsListPage';
import LeadCoordinatorsReviewPage from './pages/Management/LeadCoordinatorsReview/LeadCoordinatorsReviewPage';
import CoordinatorAddRequestsPage from './pages/Management/CoordinatorAddRequests/CoordinatorAddRequestsPage';
import FundsManagementPage from './pages/Management/FundsManagement/FundsManagementPage';
import OfflineDonationsManagementPage from './pages/Management/OfflineDonations/OfflineDonationsManagementPage';
import OpenFundRequestViewPage from './pages/Management/OpenFundRequests/OpenFundRequestViewPage';
import StandingOrdersPage from './pages/Donation/StandingOrders/StandingOrdersPage';
import DonationNewPage from './pages/Donation/DonationsAdmin/DonationNewPage';
import DonationPendingPage from './pages/Donation/DonationsAdmin/DonationPendingPage';
import DonationSettingsPage from './pages/Donation/DonationsAdmin/DonationSettingsPage';
import PublicDonationPage from './pages/Donation/PublicDonation/PublicDonationPage';
import DonationResultPage from './pages/Donation/PublicDonation/DonationResultPage';
import ManagementAreaPage from './pages/ManagementArea/ManagementAreaPage';
import TransactionsInquiryPage from './pages/ManagementArea/TransactionsInquiry/TransactionsInquiryPage';
import DonorDonationsReportPage from './pages/ManagementArea/DonorDonationsReport/DonorDonationsReportPage';
import DonorStandingOrdersPage from './pages/ManagementArea/DonorStandingOrders/DonorStandingOrdersPage';
import FeatureFlagsManagementPage from './pages/ManagementArea/FeatureFlags/FeatureFlagsManagementPage';
import FundGroupsManagementPage from './pages/ManagementArea/FundGroups/FundGroupsManagementPage';
import NedarimCredentialsManagementPage from './pages/ManagementArea/NedarimCredentials/NedarimCredentialsManagementPage';
import NedarimTransactionsFundAllocationPage from './pages/ManagementArea/NedarimTransactionsFundAllocation/NedarimTransactionsFundAllocationPage';
import LandingSiteFormsPage from './pages/ManagementArea/LandingSiteForms/LandingSiteFormsPage';
import SystemMessagesManagementPage from './pages/ManagementArea/SystemMessages/SystemMessagesManagementPage';
import UsersManagementPage from './pages/ManagementArea/Users/UsersManagementPage';
import LoginAuditPage from './pages/ManagementArea/Users/LoginAuditPage';
import LegacyManagementRedirect from './pages/ManagementArea/LegacyManagementRedirect';
import RequireFeatureRoute from './components/FeatureGate/RequireFeatureRoute';
import WorldGate from './components/FeatureGate/WorldGate';
import FundHomeRedirect from './components/FeatureGate/FundHomeRedirect';
import DashboardPreviewPage from './pages/Dashboard/DashboardPreviewPage';
import DeviceWarning from './components/DeviceWarning/DeviceWarning';
import NewVersionBanner from './components/NewVersionBanner/NewVersionBanner';
import OpenFundRequestPage from './pages/newOpenFundRequest/OpenFundRequestPage';
import { FundClassification } from './models/common/FundClassification';
import InquiriesPage from './pages/Inquiries/InquiriesPage';
import InquiriesManagementPage from './pages/Management/Inquiries/InquiriesManagementPage';
import InquiriesSettingsPage from './pages/Management/Settings/Inquiries/InquiriesSettingsPage';
import FundSettingsPage from './pages/FundSettings/FundSettingsPage';
import FundSettingsRedirect from './pages/FundSettings/FundSettingsRedirect';
import FundDocumentsPage from './pages/FundSettings/FundDocumentsPage';
import FundApiKeysPage from './pages/FundSettings/FundApiKeysPage';
import SkipToContent from './components/a11y/SkipToContent';
import RouteAnnouncer from './components/a11y/RouteAnnouncer';
import AccessibilityWidget from './components/a11y/AccessibilityWidget';
import InquiryQueueAlert from './components/Inquiries/InquiryQueueAlert';

/**
 * ניתוק (ידני, idle, או סשן ישן בטעינה) חייב להגיע ל-/login מכל מקום,
 * לא רק מ-/management. כשה-URL הוא בדיוק /new (בלי סלאש) ה-basename נשאר
 * עם pathname ריק, path="/" לא תופס, והמעטפת נשארת בלי טופס התחברות.
 */
/**
 * An address that moved, inside the fund-scoped layout: "/:fundId/<old>" -> "/:fundId/<to>".
 * `replace`, so Back does not return to the address that only redirects. The query string and
 * the hash are carried over - a link that opened a dialog or a row keeps doing so.
 */
const RedirectWithinFund = ({ to }: { to: string }) => {
  const { fundId } = useParams();
  const { search, hash } = useLocation();
  return <Navigate to={`/${fundId}/${to}${search}${hash}`} replace />;
};

const SessionAuthRedirect = () => {
  const { currentUser } = useAuthContext();
  const { pathname } = useLocation();
  const needsLogin = !currentUser && !isSessionPublicPath(pathname);

  useLayoutEffect(() => {
    if (currentUser) {
      canonicalizeBareNewBasename();
      return;
    }
    if (!needsLogin) return;
    redirectClearedSessionToLogin();
  }, [currentUser, needsLogin, pathname]);

  if (!needsLogin) return null;
  return <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  const { currentUser } = useAuthContext();
  const { t } = useTranslation();

  // HAT-1081: האתחול של Google Analytics עבר ל-ConsentProvider - הוא קורה רק
  // אחרי שהמשתמש הסכים לכלי המדידה.

  useEffect(() => {
    if (!window.location.pathname.includes('/donate')) document.title = t(`siteTitle.${import.meta.env.VITE_APP_SITE_ID}`);
  }, [t]);

  return (
    <>
      {/* חייב להיות האלמנט הראשון שמקבל מיקוד בעמוד */}
      <SkipToContent />
      <BrowserRouter basename="/new">
        <RouterGoogleAnalyticsTracker />
        <RouteAnnouncer />
        <SessionAuthRedirect />
        {/* בתוך הנתב: כשעלתה גרסה חדשה, המעבר הבא בין מסכים טוען את המסך מהשרת (SPR-10013). */}
        <NewVersionBanner />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={currentUser ? <UserPage /> : <Navigate to="/login" replace />}>
              {/* SPR-10303: «תמונת מצב» למי שהיא פתוחה לו, ולוח התנועות לכל השאר. */}
              <Route path=":fundId" element={<FundHomeRedirect />} />
              <Route
                path=":fundId"
                element={
                  <WorldGate>
                    <Outlet />
                  </WorldGate>
                }
              >
                <Route path="management/*" element={<LegacyManagementRedirect />} />
                <Route path="transactions" element={<TransactionPage />} />
                {/*
                  "תמונת מצב" - מסך הפתיחה של הקרן, מאחורי דגל התכונה 'dashboard'.
                  מי שהדגל פתוח לו גם נוחת כאן כשהוא נכנס לקרן (FundHomeRedirect, SPR-10303).

                  הדגל הוא הרשות היחידה: ההנהלה משחררת אותו ממסך ניהול הדגלים -
                  לקרן, לרכז, לסיווג או לכולם - בלי פריסה. השומר כאן מחזיר ללוח
                  התנועות גם את מי שינחש את הכתובת.

                  קדמה לזה נעילה לשתי כתובות מייל בקוד (dashboardAccess.ts), והנתיב
                  'dashboard-preview' לצידה. שניהם הוסרו: SeedFeatureFlag_Dashboard.sql
                  זורע את הדגל כ-InRollout עם מיקוד לאותן שתי כתובות, כך שהמצב
                  בייצור נשמר בדיוק.
                */}
                <Route
                  path="dashboard"
                  element={
                    <RequireFeatureRoute feature="dashboard">
                      <DashboardPreviewPage />
                    </RequireFeatureRoute>
                  }
                />
                <Route
                  path="standing-orders"
                  element={
                    <RequireFeatureRoute feature="standing-orders">
                      <StandingOrdersPage variant="credit" />
                    </RequireFeatureRoute>
                  }
                />
                {/* HAT-1135: the same board read from the accounting system's bank orders. Its own path,
                    not standing-orders/bank, so the credit menu item does not light up under it. */}
                <Route
                  path="bank-standing-orders"
                  element={
                    <RequireFeatureRoute feature="bank-standing-orders">
                      <StandingOrdersPage variant="bank" />
                    </RequireFeatureRoute>
                  }
                />
                <Route
                  path="donations/new"
                  element={
                    <RequireFeatureRoute feature="donations">
                      <DonationNewPage />
                    </RequireFeatureRoute>
                  }
                />
                <Route
                  path="donations/offline"
                  element={
                    <RequireFeatureRoute feature="donations">
                      <DonationPendingPage />
                    </RequireFeatureRoute>
                  }
                />
                <Route
                  path="donations/settings"
                  element={
                    <RequireFeatureRoute feature="donations">
                      <DonationSettingsPage />
                    </RequireFeatureRoute>
                  }
                />
                {/* Projects (מיזמים) are a section of their own (HAT-1105, 22/10/2026: "זה אמור להיות
                    בנפרד כמו בקשות תמיכה"), with two sub-tabs: the list and the suppliers.
                    They moved out from under support/goals for a reason that is visible on screen:
                    the sidebar marks every entry whose path is a PREFIX of the address, so with
                    projects at support/goals/projects both "מטרות תמיכה" and "מיזמים" were lit at
                    once. The three old addresses redirect, so a bookmark or a link in an old mail
                    still lands on the right screen. */}
                <Route path="projects/list" element={<RequireFeatureRoute feature="projects"><ProjectsPage /></RequireFeatureRoute>} />
                <Route path="projects/suppliers" element={<RequireFeatureRoute feature="projects"><ProjectSuppliersPage /></RequireFeatureRoute>} />
                <Route path="projects/new" element={<RequireFeatureRoute feature="projects"><NewProjectPage /></RequireFeatureRoute>} />
                <Route path="projects/new-supplier" element={<RequireFeatureRoute feature="projects"><NewSupplierPage /></RequireFeatureRoute>} />
                <Route path="support/goals/projects" element={<RedirectWithinFund to="projects/list" />} />
                <Route path="support/goals/projects/new" element={<RedirectWithinFund to="projects/new" />} />
                <Route path="support/goals/projects/new-supplier" element={<RedirectWithinFund to="projects/new-supplier" />} />
                {/* Kollels live under support/goals too (HAT-1446): reached from the "כוללים"
                    tile on that screen, same placement reasoning as projects above. */}
                <Route
                  path="support/goals/kollels"
                  element={
                    <RequireFeatureRoute feature="kollels">
                      <KollelsPage />
                    </RequireFeatureRoute>
                  }
                />
                <Route path="support/support-requests" element={<SupportRequestsPage />} />
                {/* Terminal rental requests (HAT-1434), under the «תוספים» group of the sidebar. */}
                <Route
                  path="addons/devices"
                  element={
                    <RequireFeatureRoute feature="device-rentals">
                      <FundPaymentDevicesPage />
                    </RequireFeatureRoute>
                  }
                />
                <Route path="support/supporteds" element={<SupportedsPage />} />
                <Route path="support/goals" element={<SupportGoalsPage />} />
                <Route
                  path="support/inquiries"
                  element={
                    <RequireFeatureRoute feature="inquiries">
                      <InquiriesPage />
                    </RequireFeatureRoute>
                  }
                />
                <Route
                  path="support/inquiries/:inquiryId"
                  element={
                    <RequireFeatureRoute feature="inquiries">
                      <InquiriesPage />
                    </RequireFeatureRoute>
                  }
                />
                <Route
                  path="fund-settings/info"
                  element={
                    <RequireFeatureRoute feature="fund-settings">
                      <FundSettingsPage />
                    </RequireFeatureRoute>
                  }
                />
                {/* HAT-1231: reachable from today's «הגדרות קרן» group (fund-settings) as well as
                    from the new top-level item (fund-documents). */}
                <Route
                  path="fund-settings/documents"
                  element={
                    <RequireFeatureRoute feature="fund-documents" orFeature="fund-settings">
                      <FundDocumentsPage />
                    </RequireFeatureRoute>
                  }
                />
                {/* HAT-1231: «משתמשים והרשאות» was folded into «הגדרות» - the representatives and
                    their permissions are a section there now. The address stays, pointing at it,
                    so a link that was handed out still lands somewhere. */}
                <Route path="fund-settings/users" element={<FundSettingsRedirect />} />
                {/* HAT-10063: the coordinator's personal API keys for the fund. */}
                <Route
                  path="fund-settings/api-keys"
                  element={
                    <RequireFeatureRoute feature="public-api">
                      <FundApiKeysPage />
                    </RequireFeatureRoute>
                  }
                />
                {/* "החשבון שלי" (HAT-1127). יושב תחת :fundId כמו כל האזור המחובר -
                    לא כי החשבון שייך לקרן, אלא כי זה מבנה הניתוב כאן וכל שאר
                    המסכים מניחים שקיימת קרן נבחרת.

                    מגודר בדגל 'my-account' כדי שההנהלה תשחרר את המסך ממסך הדגלים,
                    בלי פריסה. הדגל מגדר את המסך והכניסה אליו ולא את האנדפוינטים -
                    users/update משרת גם את מסך החשבון של האתר הישן. */}
                <Route
                  path="user-settings"
                  element={
                    <RequireFeatureRoute feature="my-account">
                      <UserSettingsPage />
                    </RequireFeatureRoute>
                  }
                />
              </Route>
            </Route>
            <Route path="/login" element={currentUser ? <Navigate to="/" /> : <LoginPage />} />
            <Route path="/register" element={<SigninPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/renew-forgotten-password" element={<RenewForgotPasswordPage />} />
            {/* "החשבון שלי" גם בלי קרן.

                הכתובת שמתחת ל-:fundId נותנת לרכז את הסיידבר כמו בכל מסך אחר, אבל
                היא דורשת קרן - ולעובדי משרד fundCodes הוא [], ולכן הפריט בתפריט
                נעלם להם לגמרי ולא הייתה להם שום דרך לשנות שם או להחליף סיסמה.
                זה בדיוק מה שהפריט בא לפתור, ולכן חשבון ללא קרן חייב כתובת משלו.
                הכותרת בוחרת: יש קרן - הנתיב שלה; אין - זה.

                redirectTo מפורש: ברירת המחדל של השומר היא '..', ומכאן זה שורש
                האתר בלי הודעה. */}
            <Route
              path="/user-settings"
              element={
                currentUser ? (
                  <RequireFeatureRoute feature="my-account" redirectTo="/">
                    <UserSettingsPage />
                  </RequireFeatureRoute>
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
            {/* האזור הפנימי היה חשוף: בלי הבדיקה הזו ניתוק השאיר את המעטפת
                על המסך, וכל ניווט ישיר לתוכו נראה כמחובר עד שקריאה כלשהי
                נפלה על 401 (HAT-1092). */}
            <Route path="/management" element={currentUser ? <ManagementAreaPage /> : <Navigate to="/login" replace />}>
              <Route index element={<Navigate to="/management/support-requests" replace />} />
              <Route path="support-requests" element={<SupportRequestsManagementPage />} />
              <Route path="classifications" element={<ClassificationsManagementPage />} />
              <Route path="children" element={<ChildrenManagementPage />} />
              <Route path="supporteds" element={<SupportedsManagementPage />} />
              <Route path="funds" element={<FundsManagementPage />} />
              <Route path="open-fund-requests" element={<OpenFundRequestsListPage />} />
              <Route path="open-fund-request-view" element={<OpenFundRequestViewPage />} />
              <Route path="lead-coordinators-review" element={<LeadCoordinatorsReviewPage />} />
              <Route path="coordinator-add-requests" element={<CoordinatorAddRequestsPage />} />
              <Route
                path="devices"
                element={
                  <RequireFeatureRoute feature="device-rentals" redirectTo="/management/support-requests">
                    <PaymentDevicesManagementPage />
                  </RequireFeatureRoute>
                }
              />
              <Route path="holiday-periods" element={<HolidayPeriodsManagementPage />} />
              <Route path="suppliers" element={<SuppliersManagementPage />} />
              {/* The suppliers-approval route is nested under projects, matching its API path
                  (management/projects/suppliers) - it is the projects flow's other queue,
                  not the general supplier directory above. */}
              <Route path="projects" element={<ProjectsManagementPage />} />
              <Route path="kollels" element={<KollelsManagementPage />} />
              <Route path="kollels/folders" element={<KollelFoldersManagementPage />} />
              <Route path="projects/suppliers" element={<SuppliersApprovalPage />} />
              <Route path="activity-domains" element={<ActivityDomainsManagementPage />} />
              <Route path="project-types" element={<ProjectTypesManagementPage />} />
              <Route path="landing-site-forms" element={<LandingSiteFormsPage />} />
              <Route path="system-messages" element={<SystemMessagesManagementPage />} />
              <Route path="eligibility-overrides" element={<EligibilityOverridesManagementPage />} />
              <Route path="third-party-payment-authorizations" element={<ThirdPartyPaymentAuthorizationsManagementPage />} />
              <Route path="offline-donations" element={<OfflineDonationsManagementPage />} />
              <Route path="transactions-inquiry" element={<TransactionsInquiryPage />} />
              <Route path="donor-donations-report" element={<DonorDonationsReportPage />} />
              {/* HAT-10024: the office's bank standing orders by donor, across funds. User-scoped flag: the
                  management area has no fund. The explicit redirect keeps a secretary inside the area. */}
              <Route
                path="donor-standing-orders"
                element={
                  <RequireFeatureRoute feature="donor-bank-standing-orders" redirectTo="/management/support-requests">
                    <DonorStandingOrdersPage />
                  </RequireFeatureRoute>
                }
              />
              <Route path="nedarim-credentials" element={<NedarimCredentialsManagementPage />} />
              <Route path="bulk-uploads" element={<BulkUploadsPage />} />
              <Route path="nedarim-sync" element={<NedarimSyncDashboardPage />} />
              {/* HAT-1441: where the office locks a collection date before sending its bank file. */}
              <Route
                path="bank-collection-locks"
                element={
                  <RequireFeatureRoute feature="bank-standing-order-actions" redirectTo="/management/support-requests">
                    <BankCollectionLocksPage />
                  </RequireFeatureRoute>
                }
              />
              <Route path="nedarim-transactions-fund-allocation" element={<NedarimTransactionsFundAllocationPage />} />
              <Route path="users" element={<UsersManagementPage />} />
              <Route path="login-audit" element={<LoginAuditPage />} />
              <Route path="feature-flags" element={<FeatureFlagsManagementPage />} />
              <Route path="fund-groups" element={<FundGroupsManagementPage />} />
              {/* An explicit redirect, not the guard's default '..': from inside the management
                  area '..' is the site root, which throws a secretary out of the area entirely
                  over one dead bookmark. The area's own landing screen is the right fallback. */}
              <Route
                path="inquiries"
                element={
                  <RequireFeatureRoute feature="inquiries" redirectTo="/management/support-requests">
                    <InquiriesManagementPage />
                  </RequireFeatureRoute>
                }
              />
              <Route
                path="inquiries/:inquiryId"
                element={
                  <RequireFeatureRoute feature="inquiries" redirectTo="/management/support-requests">
                    <InquiriesManagementPage />
                  </RequireFeatureRoute>
                }
              />
              <Route
                path="settings/inquiries"
                element={
                  <RequireFeatureRoute feature="inquiries" redirectTo="/management/support-requests">
                    <InquiriesSettingsPage />
                  </RequireFeatureRoute>
                }
              />
              <Route
                path="settings/inquiries/:tab"
                element={
                  <RequireFeatureRoute feature="inquiries" redirectTo="/management/support-requests">
                    <InquiriesSettingsPage />
                  </RequireFeatureRoute>
                }
              />
              {/* Without this, a stale bookmark (e.g. the removed /management/feedback)
                  renders the management shell around an empty outlet. */}
              <Route path="*" element={<Navigate to="/management/support-requests" replace />} />
            </Route>
          </Route>
          {import.meta.env.VITE_APP_ENABLE_PUBLIC_DONATIONS === 'true' && (
            <>
              <Route path="/donate/:fundId" element={<PublicDonationPage />} />
              <Route path="/donate/success" element={<DonationResultPage outcome="success" />} />
              <Route path="/donate/failure" element={<DonationResultPage outcome="failure" />} />
              <Route path="/donate/result" element={<DonationResultPage />} />
            </>
          )}
          <Route path="/open-fund/community" element={<OpenFundRequestPage classification={FundClassification.Community} />} />
          <Route path="/open-fund/welfare" element={<OpenFundRequestPage classification={FundClassification.Welfare} />} />
          <Route path="/open-fund/edit" element={<OpenFundRequestPage />} />
          {/* נתיב ציבורי: הבאנר עצמו והמבקרים שאינם מחוברים חייבים להגיע אליו (HAT-1081). */}
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          {/* מחוץ ל-Layout בכוונה: חלונית אישור הנהלים (HAT-1256) חוסמת את כל מה
              שבתוך המעטפת, והיא עצמה מקשרת לכאן - עמוד שיושב בתוך המעטפת היה
              נחסם על ידי אותה חלונית שממנה הגיעו אליו. ציבורי מאותה סיבה
              שהנהלים גלויים ממילא בטופס פתיחת הקרן. */}
          <Route path={REGULATIONS_PAGE_PATH} element={<RegulationsPage />} />
        </Routes>
      </BrowserRouter>
      <DeviceWarning />
      <AccessibilityWidget />
      {/* כאן ולא ב-Layout: הבאנר חייב לכסות גם את העמודים הציבוריים (donate/*, open-fund/*) שמחוץ ל-Layout. */}
      <CookieConsentBanner />
      {/* Renders nothing; alerts the office when the inquiry queue grows, from any screen. */}
      {currentUser && <InquiryQueueAlert />}
    </>
  );
};

export default App;
